import { createFileRoute, Outlet } from '@tanstack/react-router'
import { lazy, Suspense, useEffect, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { prefetchOfferChrome } from '@/hooks/use-offer'
import { prefetchReport } from '@/hooks/use-report'

const OfferDialog = lazy(() => import('./-components/companies/offer-dialog').then((m) => ({ default: m.OfferDialog })))

/**
 * Layout for a selected offer (`/offers/$offerId/*`). This route renders real content (the
 * incomplete-offer auto-open + the animated tab Outlet), so per AGENTS.md it OWNS the offerId-keyed
 * prefetches its subtree reads — the persistent chrome (getJob + applicants list) and the report data
 * shared by all three tab children. The report/board/distribution leaves keep only their own
 * tab-specific queries (board/distribution: the applicant indicators).
 *
 * Shared by the company and applicant offer views (both render `<Outlet/>` → this route). The auto-open
 * is owner-gated, so it stays inert for applicants.
 *
 * The `AnimatedOutlet` now lives in the parent `offers.tsx` (wrapping the `<Outlet/>` that renders this
 * layout) so the enter animation plays once when the report panel first appears and never re-animates on
 * tab/offer switches; this layout renders a plain `<Outlet/>` for its tab children. The auto-open's
 * dismiss state still persists across tab AND offer switches because this layout stays mounted.
 */
export const Route = createFileRoute('/_app/offers/$offerId')({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps, params }) => {
    prefetchOfferChrome(context.queryClient, deps, params)
    prefetchReport(context.queryClient, deps, params)
  },
  component: RouteComponent,
  // No pendingComponent: this passthrough layout just renders <AnimatedOutlet/>. The data-cold case is
  // covered by the parent offers.tsx (<Suspense fallback={ReportPanelSkeleton}>), and each tab leaf
  // (report/board/distribution) owns its own tab-specific skeleton — a generic one here would mismatch.
})

function RouteComponent() {
  const { offerId } = Route.useParams()
  return (
    <>
      <Suspense fallback={null}>
        <IncompleteOfferAutoOpen offerId={Number(offerId)} />
      </Suspense>
      <Outlet />
    </>
  )
}

/**
 * Auto-opens the completion dialog for an incomplete offer the viewer owns. Reads `getJob` via suspense
 * (loader-prefetched above), inside its own <Suspense fallback={null}> so a cold read never blanks the
 * tab Outlet. Dismiss state is local to this layout — it persists across tab switches (the layout stays
 * mounted) and across offer switches (same route, param change), resetting only when the selection is
 * cleared (back to the `/offers` list), which is the intended session scope.
 */
function IncompleteOfferAutoOpen(props: { offerId: number }) {
  const { data: offer } = useSuspenseQuery(trpc.jobs.getJob.queryOptions({ jobId: props.offerId }))
  const [dismissed, setDismissed] = useState<Set<number>>(() => new Set())

  // Once an offer is complete it can never auto-open, so drop any stale dismiss flag (matters only if
  // it later becomes incomplete again).
  useEffect(() => {
    if (offer.fullyRegistered) {
      setDismissed((prev) => {
        if (!prev.has(offer.id)) return prev
        const next = new Set(prev)
        next.delete(offer.id)
        return next
      })
    }
  }, [offer])

  const shouldOpen = offer.viewerRole === 'owner' && !offer.fullyRegistered && !dismissed.has(offer.id)

  // Mount the lazy dialog on first open and keep it mounted, driving visibility through `open` — a bare
  // `if (!shouldOpen) return null` would unmount it on dismiss and skip the exit animation.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    if (shouldOpen) setMounted(true)
  }, [shouldOpen])

  if (!mounted) return null

  return (
    <OfferDialog
      offerId={offer.id}
      open={shouldOpen}
      onOpenChange={(open) => {
        if (!open) setDismissed((prev) => new Set(prev).add(offer.id))
      }}
    >
      <span />
    </OfferDialog>
  )
}
